import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/senders")({
  component: SendersPage,
  head: () => ({ meta: [{ title: "Senders — InboxPulse" }] }),
});

function SendersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: senders } = useQuery({
    queryKey: ["senders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("senders")
        .select("*, domains(domain)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
  const { data: domains } = useQuery({
    queryKey: ["domains-list"],
    queryFn: async () => (await supabase.from("domains").select("id, domain")).data ?? [],
  });

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [domainId, setDomainId] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("senders").insert({
        email,
        display_name: name,
        domain_id: domainId || null,
        user_id: user!.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Sender added");
      setOpen(false);
      setEmail("");
      setName("");
      setDomainId("");
      qc.invalidateQueries({ queryKey: ["senders"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="p-8">
      <PageHeader
        title="Senders"
        description="Email addresses you send from. Each has its own health score."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[image:var(--gradient-primary)] hover:opacity-90">
                <Plus className="size-4 mr-1" /> Add sender
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add sender</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="alex@yourdomain.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Display name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Alex"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Domain</Label>
                  <select
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm"
                    value={domainId}
                    onChange={(e) => setDomainId(e.target.value)}
                  >
                    <option value="">— none —</option>
                    {domains?.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.domain}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  onClick={() => create.mutate()}
                  disabled={!email || create.isPending}
                  className="w-full"
                >
                  Add
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <HelpPanel
        title="Hilfe: Absender (Senders)"
        what={
          <>
            Hier verwaltest du die E-Mail-Adressen, von denen du verschickst (z.B.{" "}
            <code>info@deinefirma.de</code>). Jeder Absender hat einen eigenen{" "}
            <b>Gesundheits-Wert</b> (Health), der zeigt, wie gern Gmail/Outlook & Co. Mails von
            dieser Adresse annehmen.
          </>
        }
        why={
          <>
            Wenn du immer von derselben Adresse spammst, landest du im Müll. Mehrere Absender mit
            gutem Health-Score verteilen die Last und sorgen dafür, dass deine Mails im Posteingang
            ankommen.
          </>
        }
        how={[
          <>
            Oben rechts auf <b>Add sender</b> klicken.
          </>,
          <>E-Mail-Adresse und Anzeige-Name (z.B. "Max von EasyKredit") eintragen.</>,
          <>Speichern — der neue Absender erscheint in der Tabelle mit Health-Score 100.</>,
          <>
            Adresse muss zu einer Domain gehören, die du unter <b>Domains</b> verifiziert hast
            (DKIM/SPF), sonst gehen Mails in den Spam.
          </>,
          <>
            Health-Score beobachten: fällt er unter 50, weniger über diesen Absender schicken und
            mit <b>Warmup</b> langsam aufbauen.
          </>,
        ]}
        tips={[
          <>Pro Domain mindestens 2-3 Absender — das verteilt das Risiko.</>,
          <>Neuer Absender? Erst 7-14 Tage Warmup, dann erst große Kampagnen.</>,
          <>Health unter 50 = Pause einlegen, sonst landet auch der nächste Versand im Spam.</>,
        ]}
        glossary={[
          {
            term: "Health-Score",
            def: "Note 0-100, wie gut deine Absende-Adresse aktuell zugestellt wird. 80+ = top, 50-79 = ok, unter 50 = aufpassen.",
          },
          {
            term: "Warmup",
            def: "Langsames Aufwärmen einer neuen Adresse — erst wenige Mails am Tag, dann nach und nach mehr. Verhindert, dass Gmail dich als Spammer einstuft.",
          },
          {
            term: "Domain",
            def: "Der Teil hinter dem @-Zeichen, z.B. deinefirma.de. Die Domain muss eingerichtet (DKIM/SPF) sein, damit deine Mails ernst genommen werden.",
          },
        ]}
      />

      {!senders?.length ? (
        <Card className="p-12 text-center bg-card/40 border-dashed">
          <Send className="size-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">No senders yet</p>
        </Card>
      ) : (
        <Card className="bg-card/60 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Sender</th>
                <th className="text-left px-4 py-3 font-medium">Domain</th>
                <th className="text-left px-4 py-3 font-medium">Health</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {senders.map((s: any) => (
                <tr key={s.id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.display_name || s.email}</div>
                    <div className="text-xs text-muted-foreground">{s.email}</div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{s.domains?.domain ?? "—"}</td>
                  <td className="px-4 py-3">
                    <HealthBar score={s.health_score} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-accent/40">
                      {s.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function HealthBar({ score }: { score: number }) {
  const color = score >= 80 ? "bg-success" : score >= 50 ? "bg-warning" : "bg-destructive";
  return (
    <div className="flex items-center gap-2 w-40">
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-medium w-8 text-right">{score}</span>
    </div>
  );
}
